import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Form, Input, InputNumber, Space } from 'antd';
import { createPostTypeSchema, updatePostTypeSchema, type PostTypeDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { applyServerIssues, useZodForm } from '@/lib/zodForm';
import type { PostTypePrefill } from '../prefill';
import { RankMultiPicker } from './RankMultiPicker';

/**
 * Create or edit a post type.
 *
 * The `{ open, onClose, onSuccess }` convention from PAMS_CONVENTIONS §1, and every
 * validation rule comes from the shared schema through `useZodForm` — there are no
 * hand-written AntD rules anywhere in this repository.
 *
 * ⚠️ THERE IS NO GENDER FIELD ON THIS FORM AND THERE MUST NEVER BE ONE. "Women Police"
 * is a post type whose eligible rank is the Women Police RANK. If a `requiresFemale`
 * switch ever appears here, the model is wrong — see `postType.schema.ts`.
 *
 * [P7B.1 §3b] `prefill` (create only) starts the form from a link's words — the template decisions'
 * "create a post type for this column". Nothing is saved until Save, which is the whole approval.
 */
export const PostTypeDrawer = ({
  open,
  mode,
  postType,
  prefill,
  readOnly,
  onClose,
  onSuccess,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  postType?: PostTypeDto;
  prefill?: PostTypePrefill;
  readOnly: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const start = mode === 'create' ? prefill : undefined;
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const schema = mode === 'create' ? createPostTypeSchema : updatePostTypeSchema;
  const { form, field, submit, formErrors, setFormErrors } = useZodForm(schema);

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      mode === 'create'
        ? api.post<PostTypeDto>('/post-types', values)
        : api.patch<PostTypeDto>(`/post-types/${postType?.id ?? ''}`, values),
    onSuccess: () => {
      void message.success(mode === 'create' ? 'Post type created' : 'Post type updated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.postTypes.all });
      onSuccess?.();
      onClose();
    },
    onError: (error: ApiError) => {
      // Field-level 422s land on their fields; whatever is left becomes a form-level
      // alert rather than a toast that disappears while the form is still open.
      setFormErrors(applyServerIssues(form, error.details).concat(error.details ? [] : [error.message]));
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      /* Opening a row without the write capability is VIEWING, and the title says so
         rather than promising an edit every control refuses. */
      title={mode === 'create' ? 'New post type' : `${readOnly ? 'View' : 'Edit'} ${postType?.name ?? 'post type'}`}
      destroyOnHidden
      extra={
        <Space>
          {/* ⚠️ "Done", not "Close": the Drawer already ships a × whose accessible name IS
              "Close", and two controls with one name in a dialog is the same defect the
              row-action doctrine warns about. */}
          <Button onClick={onClose}>{readOnly ? 'Done' : 'Cancel'}</Button>
          {!readOnly && (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              Save
            </Button>
          )}
        </Space>
      }
    >
      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      {/*
        [P7B.1 §3b] Opened from a column decision: say which column, and how the new post type reaches it —
        by its own name or short name, the rule every template's column is matched by.
      */}
      {start?.templates ? (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          data-testid="post-type-from-templates"
          message={`For the booklet column “${start.templates.column}”.`}
          description="Keep the booklet's words as the name or the short name, and every template asking for this column is matched to it. Save takes you back to the column decisions."
        />
      ) : null}

      {/*
        ⚠️ THE GUIDANCE IS HERE BECAUSE HERE IS WHERE THE MISTAKE GETS MADE.
        The commonest wrong instinct is to create "Supervision" or "In Charge" as post
        types. Doing so turns them into countable force columns — the exact thing
        FR-5.2.6b exists to prevent — and it is not recoverable by editing a field back,
        because published policy versions would already reference them.
      */}
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="A post type is one COLUMN of deployed force."
        description={
          <div className="flex flex-col gap-2 text-sm">
            <p className="m-0">
              It is a bucket of acceptable ranks that a CP nodal enters a <strong>total required</strong> against, and
              then allocates unit by unit — <em>SI / Sgt. / Subedar</em>, <em>ASI / A-ASI</em>, <em>Unarmed Men</em>,{' '}
              <em>Women Police</em>.
            </p>
            <p className="m-0">
              <strong>Do not create post types for command roles.</strong> Supervision, In Charge and Assisting
              Inspector are defined on each <em>policy</em>, are filled with named officers, and are never counted in a
              total, an allocation or a shortfall. A post type for one of them would put it in the requirement grid,
              where it does not belong.
            </p>
            <p className="m-0 text-muted-foreground">
              The distinction is what the officer is there to DO, not their rank: an Inspector deployed as force is a
              post type; an Inspector commanding the arrangement is a command role.
            </p>
          </div>
        }
      />

      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        initialValues={{
          name: postType?.name ?? start?.name ?? '',
          shortName: postType?.shortName ?? start?.shortName ?? '',
          eligibleRankIds: postType?.eligibleRankIds ?? [],
          displayOrder: postType?.displayOrder ?? 0,
        }}
        onFinish={(values: Record<string, unknown>) => {
          // `submit()` re-parses through zod, so what reaches the API is the PARSED
          // output rather than whatever the controls happened to hold.
          const parsed = submit(values);
          if (parsed.ok) save.mutate(parsed.data);
        }}
      >
        <Form.Item
          {...field('name')}
          label="Name"
          help="Name the POST, not the rank. Where several ranks are interchangeable, say so: “SI / Sgt. / Subedar”."
        >
          <Input placeholder="SI / Sgt. / Subedar" />
        </Form.Item>

        <Form.Item
          {...field('shortName')}
          label="Short name"
          help="The requirement grid's column header. Keep it under about twelve characters — the grid has one column per post type and they have to fit side by side."
        >
          <Input placeholder="SI/Sgt/Sub" />
        </Form.Item>

        <Form.Item
          {...field('eligibleRankIds')}
          label="Eligible ranks"
          help="Which ranks may fill this post. SEVERAL IS THE NORMAL CASE — SI, Sergeant and Subedar are peers at the same seniority, so they are one post, not three. Women Police is the Women Police rank; there is no gender setting anywhere, and there does not need to be."
        >
          <RankMultiPicker />
        </Form.Item>

        <Form.Item
          {...field('displayOrder')}
          label="Display order"
          help="Lower sorts first in the picker. It does NOT fix the grid order — each policy arranges its own columns."
        >
          <InputNumber min={0} max={10_000} className="w-full" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
